import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { union } from 'lodash';
import { DateTime } from 'luxon';
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  of,
  switchMap
} from 'rxjs';
import {
  HealthTrackerUserMetricsDetailsResponse,
  HealthTrackerUserMetricsTimelineRequestParams
} from '@providers/api/health-tracker/health-tracker.interface';
import { ApiHealthTrackerService } from '@providers/api/health-tracker/health-tracker.service';
import { TrackerOverviewActions } from '../actions';
import { SLEEP_SCORE_CONFIG } from '../sleep-score/sleep-score.config';
import {
  TrackerOverviewAnalyticQueryData,
  TrackerOverviewQueryCollectionItem
} from '../states/tracker-overview.state.interface';
import { HealthTrackerDataType } from '../tracker.data-types.interface';
import { selectTrackerActive } from '../tracker.selectors';
import { TrackerService } from '../tracker.service';
import { TrackerOverviewTimeRange } from '../tracker.state.interface';

/**
 * @description Tracker Overview Effects.
 * @exports
 * @class TrackerOverviewEffects
 */
@Injectable()
export class TrackerOverviewEffects extends TrackerService {
  private readonly actions = inject(Actions);
  private readonly healthTrackerApi = inject(ApiHealthTrackerService);
  private readonly store = inject(Store);

  readonly fetchAllTrackerOverviewAnalytics = createEffect(() => {
    return this.actions.pipe(
      ofType(TrackerOverviewActions.fetchAllTrackerOverviewAnalytics),
      switchMap((action) => {
        const metricCodes = union(action.metricCodes);

        const activeTracker = this.store.selectSignal(selectTrackerActive)();
        if (!activeTracker) {
          return from(
            metricCodes.map((item) =>
              TrackerOverviewActions.fetchAllTrackerOverviewAnalyticsSuccess({
                data: {
                  metricDetailScore: [],
                  metricTimeline: []
                },
                queryType:
                  `${item}-analytics-${action.timeRange}` as TrackerOverviewQueryCollectionItem['type']
              })
            )
          );
        }

        const now = DateTime.now();
        let startedAt = DateTime.now();

        if (action.timeRange === 'day') {
          startedAt = now.startOf('day');
        } else if (action.timeRange === 'week') {
          startedAt = now.startOf('day').minus({ days: 6 });
        } else if (action.timeRange === 'month') {
          startedAt = now.startOf('day').minus({ days: 27 });
        } else if (action.timeRange === 'year') {
          startedAt = now.startOf('month').minus({ months: 11 });
        }
        let endAt = DateTime.now();

        const includeScore: HealthTrackerUserMetricsTimelineRequestParams['includeScore'] =
          ['avg'];
        if (action.timeRange === 'day') {
          includeScore.push('latest');
        }

        let timeRange: TrackerOverviewTimeRange = 'day';
        if (action.timeRange === 'day') {
          timeRange = 'hour';
        } else if (action.timeRange === 'week') {
          timeRange = 'day';
        } else if (action.timeRange === 'month') {
          timeRange = 'day';
        } else if (action.timeRange === 'year') {
          timeRange = 'month';
        }

        const requests: Array<{
          type: 'blood_pressure' | 'main-week' | 'main' | 'sleep';
          params: HealthTrackerUserMetricsTimelineRequestParams;
        }> = [];
        const timezoneOffsetMinutes = -new Date().getTimezoneOffset();
        metricCodes.forEach((metricCodeItem) => {
          if (metricCodeItem === 'SLEEP' && action.timeRange === 'day') {
            const requestIndex = requests.findIndex(
              (item) => item.type === 'sleep'
            );
            if (requestIndex > -1) {
              requests[requestIndex].params.metrics.push(metricCodeItem);
            } else {
              requests.push({
                type: 'sleep',
                params: {
                  metrics: [metricCodeItem],
                  timeFrame: timeRange,
                  startDate: startedAt
                    .minus({
                      hours: SLEEP_SCORE_CONFIG.HOURS_OFFSET_FOR_SLEEP_START
                    })
                    .toUTC()
                    .toISO(),
                  endDate: endAt.toUTC().toISO(),
                  wearableCode: activeTracker.tracker.code,
                  includeScore: includeScore,
                  timezoneOffsetMinutes
                }
              });
            }
          } else if (
            metricCodeItem === 'BLOOD_PRESSURE' &&
            action.timeRange === 'day'
          ) {
            const requestIndex = requests.findIndex(
              (item) => item.type === 'blood_pressure'
            );
            if (requestIndex > -1) {
              requests[requestIndex].params.metrics.push(metricCodeItem);
            } else {
              requests.push({
                type: 'blood_pressure',
                params: {
                  metrics: [metricCodeItem],
                  timeFrame: timeRange,
                  startDate: startedAt.toUTC().toISO(),
                  endDate: endAt.toUTC().toISO(),
                  wearableCode: activeTracker.tracker.code,
                  includeScore: includeScore,
                  timezoneOffsetMinutes
                }
              });
            }
          } else {
            const requestIndex = requests.findIndex(
              (item) => item.type === 'main'
            );
            if (requestIndex > -1) {
              requests[requestIndex].params.metrics.push(metricCodeItem);
            } else {
              requests.push({
                type: 'main',
                params: {
                  metrics: [metricCodeItem],
                  timeFrame: timeRange,
                  startDate: startedAt.toUTC().toISO(),
                  endDate: endAt.toUTC().toISO(),
                  wearableCode: activeTracker.tracker.code,
                  includeScore: includeScore,
                  timezoneOffsetMinutes
                }
              });
            }

            // For month timeRange, add week query for timeline
            if (action.timeRange === 'month') {
              const weekRequestIndex = requests.findIndex(
                (item) => item.type === 'main-week'
              );
              if (weekRequestIndex > -1) {
                requests[weekRequestIndex].params.metrics.push(metricCodeItem);
              } else {
                requests.push({
                  type: 'main-week',
                  params: {
                    metrics: [metricCodeItem],
                    timeFrame: 'week',
                    startDate: startedAt.toUTC().toISO(),
                    endDate: endAt.toUTC().toISO(),
                    wearableCode: activeTracker.tracker.code,
                    includeScore: includeScore,
                    timezoneOffsetMinutes
                  }
                });
              }
            }
          }
        });

        const metricDetailRequests: Array<
          Observable<{
            metricCode: string;
            response: HealthTrackerUserMetricsDetailsResponse;
          }>
        > = [];
        const sleepMetricExist = metricCodes.find(
          (item) => item === (HealthTrackerDataType.Sleep as string)
        );
        const bloodPressureMetricExist = metricCodes.find(
          (item) => item === (HealthTrackerDataType.BloodPressure as string)
        );
        if (sleepMetricExist && action.timeRange === 'day') {
          const request = this.healthTrackerApi
            .getUserRecordsMetricsDetailsCategory(
              {
                categoryCode: HealthTrackerDataType.Sleep
              },
              {
                startDate: startedAt
                  .minus({
                    hours: SLEEP_SCORE_CONFIG.HOURS_OFFSET_FOR_SLEEP_START
                  })
                  .toUTC()
                  .toISO(),
                endDate: endAt.toUTC().toISO(),
                wearableCode: activeTracker.tracker.code
              }
            )
            .pipe(
              map((response) => ({
                metricCode: HealthTrackerDataType.Sleep,
                response
              }))
            );
          metricDetailRequests.push(request);
        }

        if (bloodPressureMetricExist && action.timeRange === 'day') {
          const request = this.healthTrackerApi
            .getUserRecordsMetricsDetailsCategory(
              {
                categoryCode: HealthTrackerDataType.BloodPressure
              },
              {
                startDate: startedAt.toUTC().toISO(),
                endDate: endAt.toUTC().toISO(),
                wearableCode: activeTracker.tracker.code
              }
            )
            .pipe(
              map((response) => ({
                metricCode: HealthTrackerDataType.BloodPressure,
                response
              }))
            );
          metricDetailRequests.push(request);
        }

        return forkJoin({
          metricRequests: forkJoin(
            requests.map((request) =>
              this.healthTrackerApi.getUserRecordsMetricsTimeline(
                request.params
              )
            )
          ),
          metricDetailRequests:
            metricDetailRequests.length > 0
              ? forkJoin(metricDetailRequests)
              : of([])
        }).pipe(
          switchMap(({ metricRequests, metricDetailRequests }) => {
            // Combine all metrics from all responses
            const allMetrics = metricRequests.flatMap(
              (response) => response.data?.metrics ?? []
            );

            return from(
              metricCodes.map((metricCodeItem) => {
                // For month timeRange, separate week and day data
                let metricForTimeline = allMetrics.find(
                  (metricItem) => metricItem.code === metricCodeItem
                );
                let metricForDetailScore = metricForTimeline;

                if (action.timeRange === 'month') {
                  // Find the week data for timeline
                  const weekRequestIndex = requests.findIndex(
                    (item) =>
                      item.type === 'main-week' &&
                      item.params.metrics.includes(metricCodeItem)
                  );

                  if (weekRequestIndex > -1) {
                    const weekResponse = metricRequests[weekRequestIndex];
                    metricForTimeline = weekResponse.data?.metrics.find(
                      (m) => m.code === metricCodeItem
                    );
                  }

                  // Find the day data for detail score
                  const dayRequestIndex = requests.findIndex(
                    (item) =>
                      (item.type === 'main' ||
                        item.type === 'sleep' ||
                        item.type === 'blood_pressure') &&
                      item.params.metrics.includes(metricCodeItem)
                  );

                  if (dayRequestIndex > -1) {
                    const dayResponse = metricRequests[dayRequestIndex];
                    metricForDetailScore = dayResponse.data?.metrics.find(
                      (m) => m.code === metricCodeItem
                    );
                  }
                }

                const analytic: TrackerOverviewAnalyticQueryData = {
                  metricTimeline: [],
                  metricDetailScore: []
                };

                // Use week data for timeline
                if (metricForTimeline) {
                  metricForTimeline.timeline.forEach((timelineItem) => {
                    analytic.metricTimeline.push({
                      startDate: timelineItem.startDate,
                      endDate: timelineItem.endDate,
                      scores: timelineItem.score
                    });
                  });
                  analytic.metricTimeline = analytic.metricTimeline.map(
                    (item) => ({
                      ...item,
                      startDate:
                        DateTime.fromISO(item.startDate).toUTC().toISO() ?? '',
                      endDate:
                        DateTime.fromISO(item.endDate).toUTC().toISO() ?? ''
                    })
                  );
                }

                // Use day data for detail score
                if (metricForDetailScore) {
                  if (Array.isArray(metricForDetailScore.includeScore)) {
                    metricForDetailScore.includeScore.forEach((scoreItem) => {
                      analytic.metricDetailScore.push({
                        type: scoreItem.scoreFormula,
                        score: scoreItem.score,
                        startDate: scoreItem.startDate,
                        endDate: scoreItem.endDate,
                        code: scoreItem.code
                      });
                    });
                  }
                  analytic.metricDetailScore.push({
                    score: metricForDetailScore.score,
                    type: metricForDetailScore.scoreFormula,
                    startDate: startedAt.toUTC().toISO(),
                    endDate: endAt.toUTC().toISO()
                  });
                  analytic.metricDetailScore = analytic.metricDetailScore.map(
                    (score) => ({
                      ...score,
                      startDate:
                        DateTime.fromISO(score.startDate).toUTC().toISO() ?? '',
                      endDate:
                        DateTime.fromISO(score.endDate).toUTC().toISO() ?? ''
                    })
                  );

                  const blood_pressure_systolic =
                    metricForDetailScore.includeScore?.find(
                      (val) => val.code === 'blood_pressure_systolic'
                    );
                  const blood_pressure_diastolic =
                    metricForDetailScore.includeScore?.find(
                      (val) => val.code === 'blood_pressure_diastolic'
                    );

                  if (blood_pressure_systolic) {
                    analytic.metricDetailScore.push({
                      code: blood_pressure_systolic.code,
                      type: blood_pressure_systolic.scoreFormula,
                      score: blood_pressure_systolic.score,
                      startDate: blood_pressure_systolic.startDate,
                      endDate: blood_pressure_systolic.endDate
                    });
                  }

                  if (blood_pressure_diastolic) {
                    analytic.metricDetailScore.push({
                      code: blood_pressure_diastolic.code,
                      type: blood_pressure_diastolic.scoreFormula,
                      score: blood_pressure_diastolic.score,
                      startDate: blood_pressure_diastolic.startDate,
                      endDate: blood_pressure_diastolic.endDate
                    });
                  }
                }
                const metricDetail = metricDetailRequests.find(
                  (metricDetailItem) =>
                    metricDetailItem.metricCode === metricCodeItem
                );
                if (
                  Array.isArray(metricDetail?.response.data?.mappings) &&
                  metricDetail.response.data.mappings.length > 0
                ) {
                  metricDetail.response.data.mappings.forEach((mappingItem) => {
                    analytic.metricDetailScore.push({
                      type: `${mappingItem.code}_${mappingItem.scoreFormula}`,
                      score: mappingItem.score,
                      startDate: mappingItem.startTimestamp ?? '',
                      endDate: mappingItem.endTimestamp ?? ''
                    });
                  });
                }
                return TrackerOverviewActions.fetchAllTrackerOverviewAnalyticsSuccess(
                  {
                    data: analytic,
                    queryType:
                      `${metricCodeItem}-analytics-${action.timeRange}` as TrackerOverviewQueryCollectionItem['type']
                  }
                );
              })
            );
          }),
          catchError((error) => {
            return from(
              metricCodes.map((item) =>
                TrackerOverviewActions.fetchAllTrackerOverviewAnalyticsFailed({
                  error: (error as Error).message,
                  queryType:
                    `${item}-analytics-${action.timeRange}` as TrackerOverviewQueryCollectionItem['type']
                })
              )
            );
          })
        );
      })
    );
  });
}
